/**
 * @file components/shared/WeeklyReviewModal.jsx
 * @description End-of-week recap: deficit vs goal, highlights and tips.
 */

import { Lightbulb, Sparkles, TrendingDown, TrendingUp, X } from "lucide-react";

export function WeeklyReviewModal({ week, insights, onDismiss }) {
  if (!week || !insights) return null;

  const surplus = week.actualDeficit < 0;
  const goalPct =
    week.goalDeficit > 0
      ? Math.max(
          0,
          Math.min(100, Math.round((week.actualDeficit / week.goalDeficit) * 100)),
        )
      : 0;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/75 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative w-full sm:max-w-md bg-brand-surface border border-white/10 border-b-0 sm:border-b rounded-t-[2rem] sm:rounded-3xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300 max-h-[92vh] overflow-y-auto no-scrollbar">
        <button
          onClick={onDismiss}
          style={{ touchAction: "manipulation" }}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/5 text-gray-400 hover:text-white transition-colors z-10"
        >
          <X size={16} />
        </button>

        {/* Header */}
        <div
          className={`relative px-6 pt-9 pb-6 text-center bg-gradient-to-br ${
            surplus
              ? "from-red-500/20 via-orange-500/10 to-transparent"
              : "from-emerald-500/20 via-emerald-500/5 to-transparent"
          }`}
        >
          <div className="text-5xl mb-3">{insights.emoji}</div>
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-1">
            Weekly Review · {week.label}
          </p>
          <h2 className="text-xl font-bold text-white leading-snug px-2">
            {insights.headline}
          </h2>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Deficit vs Goal */}
          <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
                {surplus ? "Total Surplus" : "Total Deficit"}
              </span>
              <span
                className={`flex items-center gap-1 text-xs font-bold ${
                  surplus ? "text-red-400" : "text-emerald-400"
                }`}
              >
                {surplus ? (
                  <TrendingUp size={13} />
                ) : (
                  <TrendingDown size={13} />
                )}
                {Math.abs(week.actualDeficit).toLocaleString()} kcal
              </span>
            </div>

            {week.goalDeficit > 0 && (
              <>
                <div className="h-2.5 w-full rounded-full bg-white/5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      surplus ? "bg-red-500" : "bg-emerald-500"
                    }`}
                    style={{ width: `${surplus ? 0 : goalPct}%` }}
                  />
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  {surplus
                    ? `Target was a ${week.goalDeficit.toLocaleString()} kcal deficit.`
                    : `${goalPct}% of your ${week.goalDeficit.toLocaleString()} kcal weekly goal.`}
                </p>
              </>
            )}
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-3">
            <Stat
              value={week.avgEaten.toLocaleString()}
              unit="kcal/day"
              label="Avg Intake"
            />
            <Stat
              value={
                week.estKgChange >= 0
                  ? `-${week.estKgChange.toFixed(2)}`
                  : `+${Math.abs(week.estKgChange).toFixed(2)}`
              }
              unit="kg est."
              label="Fat Change"
              color={surplus ? "text-red-400" : "text-emerald-400"}
            />
            <Stat
              value={`${week.daysLogged}/7`}
              unit="days"
              label="Logged"
            />
          </div>

          {/* Highlights */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={13} className="text-amber-400" />
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Highlights
              </h3>
            </div>
            <ul className="space-y-2">
              {insights.highlights.map((h, i) => (
                <li
                  key={i}
                  className="flex gap-2.5 text-[13px] text-gray-300 leading-relaxed"
                >
                  <span className="text-amber-400 mt-0.5">•</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Tips */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb size={13} className="text-brand-green" />
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Tips For Next Week
              </h3>
            </div>
            <ul className="space-y-2.5">
              {insights.tips.map((t, i) => (
                <li
                  key={i}
                  className="flex gap-2.5 text-[13px] text-gray-300 leading-relaxed bg-brand-green/[0.05] border border-brand-green/10 rounded-xl p-3"
                >
                  <span className="text-brand-green mt-0.5">→</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            onClick={onDismiss}
            style={{ touchAction: "manipulation" }}
            className="w-full py-4 rounded-2xl bg-brand-green hover:bg-emerald-400 text-brand-bg font-bold text-sm uppercase tracking-widest transition-all active:scale-[0.98] shadow-[0_0_20px_rgba(16,185,129,0.3)]"
          >
            Start A Fresh Week
          </button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ value, unit, label, color = "text-white" }) => (
  <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-3 text-center">
    <p className={`text-lg font-black leading-none ${color}`}>{value}</p>
    <p className="text-[9px] text-gray-600 font-medium mt-1">{unit}</p>
    <p className="text-[9px] uppercase tracking-tighter text-gray-500 font-bold mt-1.5">
      {label}
    </p>
  </div>
);
